/**
 * Drag & drop activities (PlayspaceDrag / Order / SequencesImg / DragText
 * + PlayerElement.Dropped):
 *
 *  - drag:         items dropped onto their container (containerId). A container
 *                  accepts several items. Items = draggable count.
 *  - order:        slots in the correct order (locked ones already filled);
 *                  shuffled items dropped onto the slot with the same index.
 *  - sequencesImg: slots (elements) each expecting one option (optionId);
 *                  locked slots are pre-filled. Items = unlocked slots. An
 *                  option stays in the tray after a right drop (Unity shows it
 *                  again after 0.5 s): several slots can expect the same one.
 *  - dragText:     target words placed on the slide (text hidden); shuffled
 *                  words at the bottom dropped on the target with the same text.
 *
 * A wrong drop returns the item to its origin and counts an error (always
 * "alwaysCorrect" in Unity for these types, i.e. never ends the slide, except
 * in quiz projects where any error ends it).
 */
import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { Element as El, SlideOf } from "@makeit/schema";
import { layoutStyle } from "../Stage";
import { ElementCard, ElementImage, TextBox, useElementSound } from "../elements";
import { useActivity } from "../activity";
import { useDraggable, useDropZone, useZoneElement } from "../drag";
import { useFx } from "../fx";
import { usePlayer } from "../context";
import { shuffled } from "../util";
import { libraryUrl } from "../../data/media";
import { backgroundUrl } from "../elements";

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

export function DraggableItem({ element, onDrop, disabled, className, style, children, bg }: { element: El; onDrop: (zoneId: string | null) => boolean; disabled?: boolean; className?: string; style?: CSSProperties; children?: ReactNode; bg?: "item" | "none" }) {
  const drag = useDraggable({ onDrop: (zone) => onDrop(zone), disabled });
  return (
    <div className={`drag-item ${className ?? ""} ${disabled ? "placed" : ""}`} data-id={element.id} style={{ ...style, ...drag.style }} {...drag.handlers}>
      <ElementCard element={element} className="drag-card" bg={bg} />
      {children}
    </div>
  );
}

export function DropSlot({ id, enabled = true, className, style, children, onClick, framed }: { id: string; enabled?: boolean; className?: string; style?: CSSProperties; children?: ReactNode; onClick?: () => void; framed?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useDropZone(id, ref, enabled);
  const frame: CSSProperties | undefined = framed ? { backgroundImage: `url("${backgroundUrl("default_item")}")`, backgroundSize: "100% 100%", opacity: enabled ? 0.5 : 1 } : undefined;
  return (
    <div ref={ref} data-zone={id} className={`drop-slot ${framed ? "framed" : ""} ${className ?? ""} ${enabled ? "" : "filled"}`} style={{ ...frame, ...style }} onClick={onClick}>
      {children}
    </div>
  );
}

function SlotContent({ element, hidden }: { element: El | null; hidden?: boolean }) {
  if (!element || hidden) return null;
  return (
    <div className="slot-content">
      {element.image && <ElementImage element={element} />}
      <TextBox text={element.text} style={{ position: "absolute", inset: 0 }} fit />
    </div>
  );
}

// ---------------------------------------------------------------------------
// drag
// ---------------------------------------------------------------------------

export function DragSlide({ slide }: { slide: SlideOf<"drag"> }) {
  const { project } = usePlayer();
  const random = project.settings.randomOrderElements;
  const items = useMemo(() => (random ? shuffled(slide.draggableElements) : slide.draggableElements), [slide, random]);
  const containers = useMemo(() => (random ? shuffled(slide.containerElements) : slide.containerElements), [slide, random]);
  const activity = useActivity(Math.max(1, items.length));
  const [placed, setPlaced] = useState<Record<string, string>>({}); // itemId → containerId
  const fx = useFx();
  const zoneEl = useZoneElement();

  const drop = (item: El & { containerId: string | null }, zone: string | null) => {
    if (zone && zone === `c:${item.containerId}`) {
      setPlaced((p) => ({ ...p, [item.id]: item.containerId! }));
      fx.stars(zoneEl(zone));
      activity.correct(item.text.content);
      return true;
    }
    if (zone) activity.wrong(true, item.text.content);
    return false;
  };

  return (
    <div className="drag-activity" style={dragSizes(items.length, containers.length)}>
      <div className="drag-items">
        {items.map((it) => (
          <DraggableItem key={it.id} element={it} bg="none" disabled={!!placed[it.id]} onDrop={(z) => drop(it, z)} style={placed[it.id] ? { visibility: "hidden" } : undefined} />
        ))}
      </div>
      <div className="drag-containers">
        {containers.map((c) => (
          <Container key={c.id} element={c} />
        ))}
      </div>
    </div>
  );
}

/**
 * PlayspaceDrag.prefab: Drags row at the top (left/right 50, top 100, height
 * 400, spacing 20, square cards ≤ 400); Containers row at the bottom
 * (left/right 125, bottom 100, height 450, spacing 20); a container is the
 * M_Container-01 sprite at its native 400 px (smaller when the cells are
 * narrower). A dropped item vanishes into the box (PlayerElement.Dropped
 * destroys the dragged clone).
 */
function dragSizes(items: number, containers: number) {
  const item = Math.min(400, Math.floor((2048 - 100 - 20 * Math.max(0, items - 1)) / Math.max(1, items)));
  const box = Math.min(400, Math.floor((2048 - 250 - 20 * Math.max(0, containers - 1)) / Math.max(1, containers)));
  return { "--item": `${item}px`, "--box": `${box}px` } as CSSProperties;
}

function Container({ element }: { element: El }) {
  const { play } = useElementSound(element);
  const hasOwnImage = !!element.image;
  const bg = hasOwnImage ? undefined : `url(${libraryUrl("backgrounds/M_Container-01.png")})`;
  return (
    <DropSlot id={`c:${element.id}`} className="container" style={{ backgroundImage: bg }} onClick={play}>
      {hasOwnImage && <ElementImage element={element} />}
      {/* PlayerContainerDragElement: HorizontalOverflow = Overflow, text keeps its size */}
      <TextBox text={element.text} style={{ position: "absolute", inset: 0, whiteSpace: "nowrap", overflow: "visible", padding: 0 }} />
    </DropSlot>
  );
}

// ---------------------------------------------------------------------------
// order / sequencesImg layout (PlayspaceOrder.prefab = PlayspaceSequencesImg.prefab):
//   Containers: top 100, left/right 50, height 400, horizontal layout, spacing 20,
//               cells share the width, square cards (preserveAspect) ≤ 400.
//   Drags:      bottom row, left/right 125, height 350 (450 − 100 padding),
//               spacing 20, square cards ≤ 350.
//   Arrow (PlayerOrderArrowElement): 80 px tall, above the gap before each slot.
// ---------------------------------------------------------------------------

function orderSizes(slots: number, items: number) {
  const slot = Math.min(400, Math.floor((2048 - 100 - 20 * Math.max(0, slots - 1)) / Math.max(1, slots)));
  const item = Math.min(350, Math.floor((2048 - 250 - 20 * Math.max(0, items - 1)) / Math.max(1, items)));
  return { "--slot": `${slot}px`, "--item": `${item}px` } as CSSProperties;
}

function OrderArrow() {
  return <img className="order-arrow" src={libraryUrl("ui/orderArrow.png")} alt="" draggable={false} />;
}

// ---------------------------------------------------------------------------
// order
// ---------------------------------------------------------------------------

export function OrderSlide({ slide }: { slide: SlideOf<"order"> }) {
  const unlocked = slide.orderElements.filter((e) => !e.locked);
  const activity = useActivity(Math.max(1, unlocked.length));
  const items = useMemo(() => shuffled(unlocked), [slide]);
  const [filled, setFilled] = useState<Set<number>>(() => new Set(slide.orderElements.map((e, i) => (e.locked ? i : -1)).filter((i) => i >= 0)));
  const [placedIds, setPlacedIds] = useState<Set<string>>(new Set());
  const fx = useFx();
  const zoneEl = useZoneElement();

  const drop = (item: El, zone: string | null) => {
    const index = slide.orderElements.findIndex((e) => e.id === item.id);
    if (zone === `slot:${index}`) {
      setFilled((f) => new Set([...f, index]));
      setPlacedIds((p) => new Set([...p, item.id]));
      fx.stars(zoneEl(zone));
      activity.correct(item.text.content);
      return true;
    }
    if (zone) activity.wrong(true, item.text.content);
    return false;
  };

  return (
    <div className="order-activity" style={orderSizes(slide.orderElements.length, items.length)}>
      <div className="slots-row">
        {slide.orderElements.map((e, i) => (
          <div key={e.id} className="slot-with-arrow">
            {i > 0 && <OrderArrow />}
            <DropSlot id={`slot:${i}`} enabled={!filled.has(i)} framed className={filled.has(i) ? "filled" : "empty"}>
              <SlotContent element={e} hidden={!filled.has(i)} />
            </DropSlot>
          </div>
        ))}
      </div>
      <div className="order-items">
        {items.map((it) => (
          <DraggableItem key={it.id} element={it} bg="item" disabled={placedIds.has(it.id)} onDrop={(z) => drop(it, z)} style={placedIds.has(it.id) ? { visibility: "hidden" } : undefined} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// sequencesImg
// ---------------------------------------------------------------------------

export function SequencesImgSlide({ slide }: { slide: SlideOf<"sequencesImg"> }) {
  const byId = useMemo(() => new Map(slide.options.map((o) => [o.id, o])), [slide]);
  const unlocked = slide.elements.filter((e) => !e.locked);
  const activity = useActivity(Math.max(1, unlocked.length));
  const options = useMemo(() => shuffled(slide.options.filter((o) => o.image || o.text.content)), [slide]);
  const [filled, setFilled] = useState<Set<number>>(() => new Set(slide.elements.map((e, i) => (e.locked ? i : -1)).filter((i) => i >= 0)));
  const fx = useFx();
  const zoneEl = useZoneElement();

  const drop = (option: El, zone: string | null) => {
    if (!zone) return false;
    const index = Number(zone.replace("slot:", ""));
    const slot = slide.elements[index];
    if (slot && slot.optionId === option.id && !filled.has(index)) {
      setFilled((f) => new Set([...f, index]));
      fx.stars(zoneEl(zone));
      activity.correct(option.text.content);
      return false; // PlayerElement.Dropped › Invoke(Show): the option goes back to the tray
    }
    activity.wrong(true, option.text.content);
    return false;
  };

  return (
    <div className="order-activity" style={orderSizes(slide.elements.length, options.length)}>
      <div className="slots-row">
        {slide.elements.map((e, i) => {
          const option = e.optionId ? byId.get(e.optionId) ?? null : null;
          return (
            <div key={e.id} className="slot-with-arrow">
              {i > 0 && <OrderArrow />}
              <DropSlot id={`slot:${i}`} enabled={!filled.has(i)} framed className={filled.has(i) ? "filled" : "empty"}>
                <SlotContent element={option} hidden={!filled.has(i)} />
              </DropSlot>
            </div>
          );
        })}
      </div>
      <div className="order-items">
        {options.map((o) => (
          <DraggableItem key={o.id} element={o} bg="item" onDrop={(z) => drop(o, z)} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// dragText
// ---------------------------------------------------------------------------

export function DragTextSlide({ slide }: { slide: SlideOf<"dragText"> }) {
  const { project } = usePlayer();
  const activity = useActivity(Math.max(1, slide.draggableElements.length));
  const words = useMemo(() => {
    const list = slide.draggableElements.map((e) => ({ id: e.id, text: e.text.content }));
    return project.settings.randomOrderElements ? shuffled(list) : list;
  }, [slide, project.settings.randomOrderElements]);
  const [done, setDone] = useState<Set<string>>(new Set()); // target ids filled
  const [usedWords, setUsedWords] = useState<Set<string>>(new Set());
  const fx = useFx();
  const zoneEl = useZoneElement();

  const drop = (word: { id: string; text: string }, zone: string | null) => {
    if (!zone) return false;
    const target = slide.draggableElements.find((e) => `t:${e.id}` === zone);
    if (target && !done.has(target.id) && target.text.content === word.text) {
      setDone((d) => new Set([...d, target.id]));
      setUsedWords((u) => new Set([...u, word.id]));
      fx.stars(zoneEl(zone));
      activity.correct(word.text);
      return true;
    }
    activity.wrong(true, word.text);
    return false;
  };

  return (
    <>
      {slide.draggableElements.map((e) => (
        <DropSlot key={e.id} id={`t:${e.id}`} enabled={!done.has(e.id)} className={`dragtext-target ${done.has(e.id) ? "filled" : ""}`} style={{ ...layoutStyle(e.layout), width: Math.max(e.layout.width, 260), height: Math.max(e.layout.height, 110) }}>
          {done.has(e.id) && <TextBox text={e.text} fit />}
        </DropSlot>
      ))}
      <div className="dragtext-words">
        {words.map((w) => (
          <WordChip key={w.id} id={w.id} text={w.text} hidden={usedWords.has(w.id)} onDrop={(z) => drop(w, z)} />
        ))}
      </div>
    </>
  );
}

export function WordChip({ id, text, hidden, onDrop, onClick, className }: { id?: string; text: string; hidden?: boolean; onDrop?: (zone: string | null) => boolean; onClick?: () => void; className?: string }) {
  const drag = useDraggable({ onDrop: (z) => (onDrop ? onDrop(z) : false), disabled: hidden || !onDrop });
  // a drag ends with a click on the word: only a tap (no movement) counts as one
  const downAt = useRef<{ x: number; y: number } | null>(null);
  const handlers = onDrop ? { ...drag.handlers, onPointerDown: (e: React.PointerEvent<HTMLElement>) => ((downAt.current = { x: e.clientX, y: e.clientY }), drag.handlers.onPointerDown(e)) } : {};
  const tap = (e: React.MouseEvent) => {
    const d = downAt.current;
    if (d && Math.hypot(e.clientX - d.x, e.clientY - d.y) > 8) return;
    onClick?.();
  };
  return (
    <div className={`word-chip ${className ?? ""}`} data-id={id} dir="auto" style={{ ...drag.style, visibility: hidden ? "hidden" : undefined }} {...handlers} onClick={tap}>
      {text}
    </div>
  );
}
